import {
  FORMAT_OPTIONS,
  RADIUS_PRESETS,
  SORT_OPTIONS,
} from '../lib/filters.js';

/** All filter controls. Every one of these runs client-side over the merged list. */
export default function FilterBar({
  filters,
  onChange,
  availableThemes,
  hasLocation,
  onReset,
}) {
  const update = (patch) => onChange({ ...filters, ...patch });

  const toggleFormat = (id) => {
    const next = filters.formats.includes(id)
      ? filters.formats.filter((f) => f !== id)
      : [...filters.formats, id];
    // Never allow an empty format set — it would blank the page with no obvious cause.
    update({ formats: next.length > 0 ? next : filters.formats });
  };

  const toggleTheme = (theme) => {
    const next = filters.themes.includes(theme)
      ? filters.themes.filter((t) => t !== theme)
      : [...filters.themes, theme];
    update({ themes: next });
  };

  return (
    <section className="filters" aria-label="Filters">
      <div className="filter-row">
        <label className="filter-field filter-search">
          <span className="filter-label">Search</span>
          <input
            type="search"
            className="input"
            placeholder="Name, city, platform…"
            value={filters.query}
            onChange={(event) => update({ query: event.target.value })}
          />
        </label>

        <div className="filter-field">
          <span className="filter-label">
            Travel radius
            {!hasLocation ? <span className="filter-hint"> · set a location first</span> : null}
          </span>
          <div className="segmented" role="group" aria-label="Travel radius">
            {RADIUS_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`segment ${filters.radius === preset.id ? 'is-active' : ''}`}
                onClick={() => update({ radius: preset.id })}
                disabled={!hasLocation && preset.id !== 'anywhere'}
                title={
                  !hasLocation && preset.id !== 'anywhere'
                    ? 'Set your location to filter by distance'
                    : undefined
                }
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-field">
          <span className="filter-label">Format</span>
          <div className="segmented" role="group" aria-label="Format">
            {FORMAT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`segment ${filters.formats.includes(option.id) ? 'is-active' : ''}`}
                onClick={() => toggleFormat(option.id)}
                aria-pressed={filters.formats.includes(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="filter-row">
        <label className="filter-field">
          <span className="filter-label">From</span>
          <input
            type="date"
            className="input"
            value={filters.dateFrom}
            onChange={(event) => update({ dateFrom: event.target.value })}
          />
        </label>

        <label className="filter-field">
          <span className="filter-label">To</span>
          <input
            type="date"
            className="input"
            value={filters.dateTo}
            onChange={(event) => update({ dateTo: event.target.value })}
          />
        </label>

        <label className="filter-field">
          <span className="filter-label">Sort by</span>
          <select
            className="input"
            value={filters.sort}
            onChange={(event) => update({ sort: event.target.value })}
          >
            {SORT_OPTIONS.map((option) => (
              <option
                key={option.id}
                value={option.id}
                disabled={option.id === 'distance' && !hasLocation}
              >
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button type="button" className="button button-ghost filter-reset" onClick={onReset}>
          Reset
        </button>
      </div>

      {availableThemes.length > 0 ? (
        <div className="filter-themes">
          <span className="filter-label">Themes</span>
          <div className="chips">
            {availableThemes.slice(0, 18).map(({ theme, count }) => (
              <button
                key={theme}
                type="button"
                className={`chip ${filters.themes.includes(theme) ? 'is-active' : ''}`}
                onClick={() => toggleTheme(theme)}
                aria-pressed={filters.themes.includes(theme)}
              >
                {theme}
                <span className="chip-count">{count}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
